/**
 * API methods that rely on both Duolingo TTS and TTS Generation Backend
 */

import Axios from "axios";

import type { DuolingoTtsProvider, DuolingotTtsVoiceData } from "./duolingoTts";
import {
  getDuolingoTtsRecordedVoices,
  getDuolingoTtsVoices,
} from "./duolingoTts";
import type {
  TtsGenerationAge,
  TtsGenerationGender,
  TtsGenerationProvider,
  VoicesV2Item,
} from "./ttsGenerationBackend";
import { getTtsGenerationVoiceList } from "./ttsGenerationBackend";

export interface VoiceListVoice extends Omit<VoicesV2Item, "provider"> {
  readonly addedAt: Date | undefined;
  getRawData: () => VoicesV2Item | DuolingotTtsVoiceData;
  isUsedInLearningApp: boolean;
  provider: TtsGenerationProvider | "recorded";
}

const areProvidersEqual = (
  ttsGenerationProvider: TtsGenerationProvider,
  duolingoTtsProvider: DuolingoTtsProvider,
) =>
  (ttsGenerationProvider === "amazon" && duolingoTtsProvider === "ivona") ||
  ttsGenerationProvider === duolingoTtsProvider;

export const getCombinedVoiceList = async (): Promise<VoiceListVoice[]> => {
  const [ttsGenList, duolingoTtsList, recordedVoicesList] = await Promise.all([
    getTtsGenerationVoiceList(),
    getDuolingoTtsVoices(),
    getDuolingoTtsRecordedVoices(),
  ]);

  const ttsVoices = ttsGenList.map(ttsGenVoice => {
    const isUsedInLearningApp =
      duolingoTtsList?.find(
        duolingoTtsVoice =>
          // Amazon and Azure custom voices match on their model name,
          // Google and Azure normal voices on their name
          [
            ttsGenVoice.name,
            ttsGenVoice.provider_parameters.model_name,
          ].includes(duolingoTtsVoice.name) &&
          // It is possible that a provider has a voice whose short name overlaps with our name
          // for a voice of a different provider in a different language (e.g., Microsoft's
          // en-US-CoraNeural and Google's el-GR-Wavenet-A are both Cora)
          areProvidersEqual(ttsGenVoice.provider, duolingoTtsVoice.provider),
      ) !== undefined;

    return toVoiceListVoice(ttsGenVoice, isUsedInLearningApp);
  });
  const recordedVoices =
    recordedVoicesList?.map(recordedVoice => ({
      added_at: null,
      added_by: "Duolingo",
      get addedAt() {
        return undefined;
      },
      age: recordedVoice.age as TtsGenerationAge,
      friendly_name: null,
      gender: recordedVoice.gender as TtsGenerationGender,
      getRawData: () => recordedVoice,
      id: recordedVoice.name,
      is_default_version: true,
      is_expressive: false,
      is_multilingual: false,
      isUsedInLearningApp: true,
      language_statuses: [],
      last_updated_at: null,
      last_updated_by: "Duolingo",
      name: recordedVoice.name,
      notes: "",
      primary_language: recordedVoice.language,
      provider: "recorded" as const,
      provider_parameters: {
        model_name: "recorded",
      },
      speed: null,
      status: "active" as const,
      tags: [],
      use_cases: [],
      version: 1,
      world_character: null,
    })) ?? [];
  return [...ttsVoices, ...recordedVoices];
};

export const getAudioMp3 = async (url: string) =>
  (
    await Axios.get<Blob>(url, {
      responseType: "blob",
    })
  ).data;

export const toVoiceListVoice = (
  voice: VoicesV2Item,
  isUsedInLearningApp: boolean,
): VoiceListVoice => ({
  ...voice,
  get addedAt() {
    return voice.added_at === null ? undefined : new Date(voice.added_at);
  },
  getRawData: () => voice,
  isUsedInLearningApp,
});
